import { CTS_MARK_COLOR, CTS_MARK_RIM } from './simulatorPalette';
import { UP_COLOR, DOWN_COLOR } from './gannConfluenceOverlay';

/**
 * HOW A CTS TIMING MARK IS PAINTED, in one place, for the two builders that produce them.
 *
 * <h2>Three states, and the fill is the only thing that varies</h2>
 *
 * <pre>
 *   drawn AT ITS PRICE, signal up     green triangle, upright
 *   drawn AT ITS PRICE, signal down   red triangle, rotated 180
 *   drawn ON THE RAIL                 amber triangle, upright, no direction claimed
 * </pre>
 *
 * Same shape and same rim in all three, so a reader learns one mark and reads its fill. Which of
 * the three a given point lands in is decided by the RENDERER, not here: this supplies both
 * candidate fills and `chartDataProcessor` picks between them from whether the bar has a revealed
 * price. That split is the point — the builder knows the signal, and only the renderer knows how
 * far the playhead has got.
 *
 * <h2>⚠ A CLAIM PARTLY REVERSED, and which half survives</h2>
 *
 * On 2026-10-21 every CTS mark became one neutral amber, on the reasoning that a triangle at a
 * price asserts a LEVEL and a DIRECTION, and a mark drawn ahead of the playhead asserts both about
 * a bar nobody has seen. That reasoning is intact and is why the rail half is still neutral.
 *
 * What it over-corrected is the other half: on a bar whose price is ON SCREEN the direction is
 * read from price action the reader can see for themselves, so withholding it threw away a real
 * reading to avoid an unreal one. The green and red are the study's OWN
 * {@link UP_COLOR}/{@link DOWN_COLOR}, reinstated rather than chosen — customers of this study
 * already know them, and soft-deleting them yesterday is what left them there to reinstate.
 *
 * <h2>The direction is carried on TWO channels, deliberately</h2>
 *
 * Fill AND rotation. A green upright triangle and a red inverted one say the same thing twice, so
 * the mark survives a monochrome screenshot and a colour-blind reader. Measured by luminance the
 * two fills are only 1.10:1 apart — they differ by HUE — so the rotation is not decoration.
 *
 * <h2>Why these two need no rim and keep one anyway</h2>
 *
 * The rim exists because amber is 1.56:1 on the light canvas. Green and red do not need it —
 * measured against this application's two canvases:
 *
 * <pre>
 *   #2e7d32 green   4.92 : 1 on light    3.69 : 1 on dark
 *   #c62828 red     5.39                 3.37
 *   #FFC107 amber   1.56                11.61   <- the one that needs the rim
 * </pre>
 *
 * Both clear the 3:1 floor on both canvases unaided. The rim is kept on all three anyway so that
 * the fill is the ONLY variable across the states — a rim that came and went would be a second
 * channel saying something the fill already says, and it costs nothing where it is not needed
 * (1.02:1 against the dark canvas, i.e. invisible).
 */

/** The fill a mark takes when it is drawn on the rail — no price, so no direction. */
export const CTS_RAIL_COLOR = CTS_MARK_COLOR;

/**
 * The per-point style for a CTS mark whose direction is KNOWN.
 *
 * <p>`position` is deliberately NOT returned. It is the direction channel the Trade Simulator and
 * the odds engine read, it doubles as the placement, and one builder overrides it for a column the
 * market has not traded — so it stays at the call sites where that override is visible.
 *
 * @param {boolean} up true when the signal reads up / long.
 */
export function ctsMarkStyle(up) {
  return {
    symbol: 'triangle',
    // Only the down arrow is rotated: 'triangle' already points up.
    ...(up ? {} : { symbolRotate: 180 }),
    color: up ? UP_COLOR : DOWN_COLOR,
    colorRole: up ? 'ctsUp' : 'ctsDown',
    // The fallback pair, used by the renderer when this point turns out to have no revealed price.
    railColor: CTS_RAIL_COLOR,
    railColorRole: 'ctsMark',
    borderColor: CTS_MARK_RIM,
    borderWidth: 1,
  };
}

/**
 * The style for a mark that can have NO direction — a column the market has not reached, so its
 * "direction" would have been read off a placeholder bar.
 *
 * <p>It is a yellow triangle rather than the gold circle this case used to take. That circle was
 * a workaround for having nowhere to put such a mark: read off a padded bar, it was anchored at
 * the last real close, which lined every forward mark up on one horizontal row of green and red
 * in empty space. The rail is the proper answer to that, and with the mark at the foot of the grid
 * claiming no level, the separate shape is no longer carrying anything.
 */
export function ctsNeutralMarkStyle() {
  return {
    symbol: 'triangle',
    color: CTS_RAIL_COLOR,
    colorRole: 'ctsMark',
    railColor: CTS_RAIL_COLOR,
    railColorRole: 'ctsMark',
    borderColor: CTS_MARK_RIM,
    borderWidth: 1,
  };
}
